import { useMemo, useEffect, useLayoutEffect } from "react";
import { View } from "react-native";
import { CommonActions, StackActions } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  getDataPointsForTest,
  getDataPointForMarkerName,
} from "@src/packages/content";
import type { ContentBlock } from "@src/packages/content";
import { useLocale } from "@src/packages/i18n";
import type { AccessStackParamList } from "../../types";
import { TestDetailContent } from "../../components/TestDetailSheet";
import { getBiomarkerFactsheet } from "@src/packages/test-results/content/biomarker-factsheets";
import type { FactSheetLinkTarget } from "../../components/LinkedFactSheetText";
import { useSmartBack } from "../../hooks/useSmartBack";
import {
  CANONICAL_ENDOMETRIAL_TEST_IDS,
  getTestRouteId,
  resolveRouteTestId,
  TEST_PRODUCT_CONTENT_ROUTES,
} from "./testRouteIds";

type Props =
  | NativeStackScreenProps<AccessStackParamList, "TestDetail">
  | NativeStackScreenProps<AccessStackParamList, "TestProductDetail">;

function hasConsolidatedBiomarkerRoute(item: ContentBlock<"test">): boolean {
  const dataPoints = getDataPointsForTest(item.id);
  if (dataPoints.length !== 1) return false;

  const dataPoint = dataPoints[0];
  if (!dataPoint?.body.sampleTypes?.includes("blood")) return false;

  const markerName = dataPoint?.title ?? item.body.testName ?? item.title;
  return Boolean(
    getDataPointForMarkerName(markerName) && getBiomarkerFactsheet(markerName),
  );
}

function hasCanonicalDataPointRoute(item: ContentBlock<"test">): boolean {
  const dataPoints = getDataPointsForTest(item.id);

  // Canonical data-point relationships are not limited to lab panels. Imaging
  // and monitoring tests use the same tabbed sheet, while single lab markers
  // with a dedicated biomarker factsheet keep the richer marker route below.
  if (dataPoints.length > 1) return true;
  if (dataPoints.length !== 1) return false;

  return !hasConsolidatedBiomarkerRoute(item);
}

function hasPgtRoute(item: ContentBlock<"test">): boolean {
  return [
    "test-preimplantation-genetic-testing",
    "test-pgt-a",
    "test-pgt-m",
    "test-pgt-sr",
    "test-pgt-p",
  ].includes(String(item.id));
}

export default function TestDetailScreen({ route, navigation }: Props) {
  const { locale } = useLocale();
  const handleClose = useSmartBack({
    returnTab: route.params.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "Testing",
  });
  const resolvedTestId = useMemo(
    () => resolveRouteTestId(route.params),
    [route.params],
  );
  const candidateIds = useMemo(
    () => [
      resolvedTestId,
      resolvedTestId.startsWith("test-")
        ? resolvedTestId
        : `test-${resolvedTestId}`,
    ],
    [resolvedTestId],
  );

  const item = useMemo(() => {
    void locale;
    const tests = getBlocksByKind("test") as ContentBlock<"test">[];
    const products = getBlocksByKind("product") as ContentBlock<"product">[];
    return (
      tests.find((test) => candidateIds.includes(test.id as string)) ??
      products.find((product) => candidateIds.includes(product.id as string)) ??
      null
    );
  }, [candidateIds, locale]);

  const shouldUseComparison =
    item?.kind === "test" && hasConsolidatedBiomarkerRoute(item);
  const shouldUsePanelComparison =
    item?.kind === "test" && hasCanonicalDataPointRoute(item);
  const shouldUsePgtComparison = item?.kind === "test" && hasPgtRoute(item);
  const shouldUseEndometrialAssayComparison =
    item?.kind === "test" && CANONICAL_ENDOMETRIAL_TEST_IDS.has(item.id);

  useEffect(() => {
    if (!item) {
      navigation.goBack();
      return;
    }
    if (shouldUsePgtComparison) {
      navigation.dispatch(
        StackActions.replace("PgtComparison", {
          testId: String(item.id),
          returnTab: route.params.returnTab,
        }),
      );
    } else if (shouldUseEndometrialAssayComparison) {
      navigation.dispatch(
        StackActions.replace("EndometrialAssayComparison", {
          testId: String(item.id),
          returnTab: route.params.returnTab,
        }),
      );
    } else if (shouldUsePanelComparison) {
      navigation.dispatch(
        StackActions.replace("TestPanelComparison", {
          testId: String(item.id),
          returnTab: route.params.returnTab,
        }),
      );
    } else if (shouldUseComparison) {
      navigation.dispatch(
        StackActions.replace("TestComparison", {
          testId: String(item.id),
          returnTab: route.params.returnTab,
        }),
      );
    }
  }, [
    item,
    navigation,
    route.params.returnTab,
    shouldUseComparison,
    shouldUsePanelComparison,
    shouldUsePgtComparison,
    shouldUseEndometrialAssayComparison,
  ]);

  useLayoutEffect(() => {
    if (
      item &&
      !shouldUseComparison &&
      !shouldUsePanelComparison &&
      !shouldUsePgtComparison &&
      !shouldUseEndometrialAssayComparison
    ) {
      navigation.setOptions({ title: item.title });
    }
  }, [
    item,
    navigation,
    shouldUseComparison,
    shouldUsePanelComparison,
    shouldUsePgtComparison,
    shouldUseEndometrialAssayComparison,
  ]);

  const openFactSheetLink = (target: FactSheetLinkTarget) => {
    switch (target.kind) {
      case "condition":
        navigation.navigate("ConditionDetail", { conditionId: target.id });
        return;
      case "supplement":
        navigation.navigate("SupplementDetail", { supplementId: target.id });
        return;
      case "test":
        if (TEST_PRODUCT_CONTENT_ROUTES[target.id]) {
          navigation.dispatch(
            StackActions.push(
              "TestProductDetail",
              TEST_PRODUCT_CONTENT_ROUTES[target.id],
            ),
          );
          return;
        }
        navigation.dispatch(
          StackActions.push("TestDetail", {
            testId: getTestRouteId(target.id),
          }),
        );
        return;
      case "therapy":
        navigation.navigate("TherapyDetail", { therapyId: target.id });
        return;
      case "medication":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "MedicationDetail",
              params: { medicationId: target.id },
            },
          }),
        );
        return;
      case "procedure":
        navigation.getParent()?.dispatch(
          CommonActions.navigate({
            name: "Learn",
            params: {
              screen: "ProcedureDetail",
              params: { procedureId: target.id },
            },
          }),
        );
        return;
    }
  };

  if (
    !item ||
    shouldUseComparison ||
    shouldUsePanelComparison ||
    shouldUsePgtComparison ||
    shouldUseEndometrialAssayComparison
  ) {
    return null;
  }

  const commercialCategoryId =
    item.kind === "test"
      ? item.body.commercialProductProfile?.categoryTestId
      : null;
  const handleBack = commercialCategoryId
    ? () => {
        const state = navigation.getState();
        const currentIndex = state.routes.findIndex(
          (stateRoute) => stateRoute.key === route.key,
        );
        const previousRoute =
          currentIndex > 0 ? state.routes[currentIndex - 1] : undefined;

        if (
          previousRoute?.name === "TestDetail" ||
          previousRoute?.name === "TestProductDetail"
        ) {
          navigation.goBack();
          return;
        }

        navigation.dispatch(
          StackActions.replace("TestDetail", {
            testId: getTestRouteId(String(commercialCategoryId)),
          }),
        );
      }
    : undefined;

  return (
    <View style={{ flex: 1 }}>
      <TestDetailContent
        item={item}
        onClose={handleClose}
        onBack={handleBack}
        onOpenMetric={(metricKey) => {
          navigation.getParent()?.dispatch(
            CommonActions.navigate({
              name: "Care",
              params: {
                screen: "MetricDetail",
                params: { metricKey },
              },
            }),
          );
        }}
        onOpenFactSheet={openFactSheetLink}
        skipExitAnimation
      />
    </View>
  );
}
